import {
  IMPLICIT_PRIMITIVE_LABELS,
  ImplicitPrimitive,
  NASA_LATTICE_HEROES,
  NasaLatticeTopology,
} from '@/config/implicitPrimitives';

const DOMAIN_HERO: Record<string, { src: string; flip: boolean; alt: string }> =
  {
    sea: { src: '/sea-power-hero.png', flip: true, alt: 'Sea' },
    air: { src: '/air-power-hero.png', flip: false, alt: 'Air' },
    land: { src: '/land-vehicle-hero.png', flip: false, alt: 'Land' },
    space: { src: '/space-power-hero.png', flip: true, alt: 'Space' },
  };

interface PromptHeroProps {
  textToCadSelected: boolean;
  vehicleSubTab: string;
  implicitPrimitive: ImplicitPrimitive;
  vehicleEnabled: boolean;
  isLandWheels: boolean;
  isSpaceHeatExchanger: boolean;
  vehicleTab: string;
  supinInletType: string;
  isMobile: boolean;
}

export function PromptHero({
  textToCadSelected,
  vehicleSubTab,
  implicitPrimitive,
  vehicleEnabled,
  isLandWheels,
  isSpaceHeatExchanger,
  vehicleTab,
  supinInletType,
  isMobile,
}: PromptHeroProps) {
  // NASA Lattice Primitives sub-tab lives under the Text-or-
  // Image-to-CAD domain (textToCadSelected), separate from the
  // vehicleEnabled subHero pipeline below. When a heroed
  // topology is active, its hero replaces the center stage —
  // matches the pattern SUPIN/Wheels use under vehicleEnabled.
  if (
    textToCadSelected &&
    vehicleSubTab === 'nasa-lattice-primitives' &&
    implicitPrimitive.startsWith('nasa-lattice-')
  ) {
    const hero = NASA_LATTICE_HEROES[implicitPrimitive as NasaLatticeTopology];
    if (hero) {
      return (
        <img
          src={hero}
          alt={IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive]}
          className="aspect-video w-full max-w-[36rem] rounded-xl object-contain"
        />
      );
    }
  }
  return vehicleEnabled ? (
    (() => {
      // Sub-tab hero override. When the active sub-tab has its own
      // hero image, that hero replaces the domain hero at the same
      // size/shape — applies uniformly across every domain. Falls
      // through to the domain default when the sub-tab has no
      // associated hero (or no sub-tab is selected yet).
      let subHero: { src: string; alt: string } | undefined;
      if (isLandWheels && implicitPrimitive === 'rover-wheel') {
        subHero = {
          src: '/lunar-rover-wheel-hero.png',
          alt: 'Lunar Rover Wheel',
        };
      } else if (isSpaceHeatExchanger && implicitPrimitive === 'helix-heat-x') {
        subHero = {
          src: '/heatx-hero.png',
          alt: 'Helical Heat Exchanger',
        };
      } else if (vehicleTab === 'space' && vehicleSubTab === 'rocket-nozzle') {
        subHero = {
          src: '/rocket-nozzle-tab-hero.png',
          alt: 'Rocket Nozzle',
        };
      } else if (
        vehicleTab === 'air' &&
        vehicleSubTab === 'nasa-supersonic-inlet'
      ) {
        if (supinInletType === 'K1') {
          subHero = {
            src: '/supin-transonic-inlet-hero.png',
            alt: 'Transonic Inlet',
          };
        } else if (supinInletType === 'K3') {
          subHero = {
            src: '/supin-spike-inlet-hero.png',
            alt: 'Spike Inlet',
          };
        } else if (supinInletType === 'K4') {
          subHero = {
            src: '/supin-rectangular-inlet-hero.png',
            alt: 'Rectangular Inlet',
          };
        }
      }
      if (subHero) {
        return (
          <img
            src={subHero.src}
            alt={subHero.alt}
            className="aspect-video w-full max-w-[36rem] rounded-xl object-contain"
          />
        );
      }
      const hero = DOMAIN_HERO[vehicleTab];
      return hero ? (
        <img
          src={hero.src}
          alt={hero.alt}
          className="aspect-video w-full max-w-[36rem] rounded-xl object-contain"
          style={hero.flip ? { transform: 'scaleX(-1)' } : undefined}
        />
      ) : null;
    })()
  ) : isMobile ? (
    /* Mobile default: land hero */
    (() => {
      const hero = DOMAIN_HERO['land'];
      return (
        <img
          src={hero.src}
          alt={hero.alt}
          className="aspect-video w-full max-w-[36rem] rounded-xl object-contain"
        />
      );
    })()
  ) : (
    /* Desktop default: four domain heroes */
    <div className="flex gap-1.5">
      {[
        { src: '/sea-power-hero.png', alt: 'Sea', flip: true },
        { src: '/air-power-hero.png', alt: 'Air', flip: false },
        { src: '/land-vehicle-hero.png', alt: 'Land', flip: false },
        { src: '/space-power-hero.png', alt: 'Space', flip: true },
      ].map(({ src, alt, flip }) => (
        <img
          key={src}
          src={src}
          alt={alt}
          className="h-64 w-64 rounded-lg object-contain"
          style={flip ? { transform: 'scaleX(-1)' } : undefined}
        />
      ))}
    </div>
  );
}
